import { useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Sparkles, Loader2, Copy, Instagram } from "lucide-react";

const PRESETS = [
    "New skin quiz drop — take it, get 50% off",
    "Same-day booking — before 6pm",
    "Emergency 30-min service reveal",
    "AI 3D nail art printer teaser",
    "Bridal package announcement",
    "Facial of the week — Signature Glow",
    "Vegas showgirl nail set — client feature",
    "Mobile beauty in Jacksonville explainer",
];

export default function AdminInstagram() {
    const { user, loading } = useAuth();
    const [topic, setTopic] = useState("");
    const [tone, setTone] = useState("sassy luxurious");
    const [caption, setCaption] = useState("");
    const [busy, setBusy] = useState(false);

    if (loading) return <div className="p-10 text-center text-white/50">Loading…</div>;
    if (!user) return <Navigate to="/login" replace />;
    if (user.role !== "admin") {
        return (
            <div className="max-w-xl mx-auto mt-10 p-8 rounded-2xl border border-rose/30 bg-rose/5 text-center">
                <p className="font-serif text-2xl">Admin only</p>
                <Link to="/portal" className="inline-block mt-5 text-rose hover:underline">← Back to portal</Link>
            </div>
        );
    }

    const generate = async () => {
        if (!topic.trim()) return toast.error("Give me a topic first");
        setBusy(true);
        try {
            const { data } = await api.post("/ai/instagram-caption", { topic, tone });
            setCaption(data.caption || "");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "AI unavailable");
        }
        setBusy(false);
    };

    const copy = () => {
        navigator.clipboard.writeText(caption);
        toast.success("Copied — paste it into Instagram");
    };

    return (
        <div className="max-w-3xl mx-auto" data-testid="admin-ig-page">
            <Link to="/portal" className="text-xs text-white/55 hover:text-white inline-flex items-center gap-1 mb-2">
                <ArrowLeft className="w-3 h-3" /> Back to portal
            </Link>
            <p className="text-xs tracking-[0.3em] uppercase text-gold">Admin</p>
            <h1 className="font-serif text-4xl sm:text-5xl mt-1 flex items-center gap-3">
                <Instagram className="w-8 h-8 text-rose" />
                Instagram Caption Generator
            </h1>
            <p className="mt-2 text-white/55 max-w-xl mb-8">
                AI writes an on-brand caption + hashtags for you. Copy → paste into Instagram.
            </p>

            <div className="space-y-4">
                <div>
                    <p className="text-[10px] uppercase tracking-widest text-white/45 mb-2">Topic</p>
                    <input
                        value={topic}
                        onChange={(e) => setTopic(e.target.value)}
                        placeholder="What's the post about?"
                        data-testid="ig-topic"
                        className="w-full bg-surface border border-white/10 focus:border-gold/50 rounded-xl px-4 py-3 outline-none"
                    />
                    <div className="mt-2 flex flex-wrap gap-2">
                        {PRESETS.map((p) => (
                            <button
                                key={p}
                                type="button"
                                onClick={() => setTopic(p)}
                                className="text-xs px-3 py-1.5 rounded-full border border-white/10 text-white/60 hover:border-gold/40 hover:text-gold transition"
                            >
                                {p}
                            </button>
                        ))}
                    </div>
                </div>

                <div>
                    <p className="text-[10px] uppercase tracking-widest text-white/45 mb-2">Tone</p>
                    <select
                        value={tone}
                        onChange={(e) => setTone(e.target.value)}
                        data-testid="ig-tone"
                        className="w-full bg-surface border border-white/10 focus:border-gold/50 rounded-xl px-4 py-3 outline-none"
                    >
                        <option>sassy luxurious</option>
                        <option>warm friendly</option>
                        <option>bold Vegas</option>
                        <option>quiet luxury</option>
                        <option>bestie casual</option>
                    </select>
                </div>

                <button
                    type="button"
                    onClick={generate}
                    disabled={busy}
                    data-testid="ig-generate"
                    className="px-6 py-3 rounded-full bg-gradient-to-r from-gold via-rose to-gold text-ink font-medium hover:opacity-90 transition disabled:opacity-50 flex items-center gap-2"
                >
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                    Generate caption
                </button>

                {caption && (
                    <div className="relative mt-6 p-6 rounded-2xl border border-gold/30 bg-ink/50" data-testid="ig-output">
                        <button
                            onClick={copy}
                            title="Copy"
                            className="absolute top-3 right-3 p-2 rounded-full text-white/55 hover:text-gold hover:bg-white/5 transition"
                        >
                            <Copy className="w-4 h-4" />
                        </button>
                        <pre className="whitespace-pre-wrap font-serif text-base leading-relaxed pr-8 text-white">
                            {caption}
                        </pre>
                    </div>
                )}
            </div>
        </div>
    );
}
